<template>
  <v-dialog v-model="show" :persistent="loading" :fullscreen="isMobile"
    max-width="740" content-class="quote-dialog" no-click-animation>
    <v-card class="contact-form">
      <header class="quote-header">
        <div class="quote-brand"><img :src="logoUrl" alt="Velodrom"><span>YOUR CUSTOM BUILD</span></div>
        <button class="quote-close" type="button" aria-label="Close quote form"
          :disabled="loading" @click="show = false"><v-icon color="white">$close</v-icon></button>
        <p class="quote-eyebrow">{{ reply.today ? 'CONTACT TODAY · ANSWERS TODAY' : 'FROM YOUR SCREEN TO THE ROAD' }}</p>
        <h2>{{ reply.today ? 'Your build, answered today.' : 'Let’s make it your next ride.' }}</h2>
        <p>Tell us a little about yourself. A specialist will review your build, check every detail and come back with availability, fit and your quote.</p>
      </header>

      <div v-if="sended" class="quote-success" role="status">
        <div class="quote-success-icon">✓</div>
        <h3>Your next ride is one step closer.</h3>
        <p>We’ve received your enquiry and bike configuration. {{ reply.today ? 'A specialist will be in touch today.' : reply.text.replace('Send it now and a', 'A') }}</p>
        <Btn color="bb-primary" dark @click="show = false">Back to my build</Btn>
      </div>

      <v-form v-else ref="form" v-model="valid" class="quote-form" @submit.prevent="send">
        <div class="quote-context quote-promise"><v-icon size="20" color="#304635">mdi-clock-fast</v-icon>
          <span><strong>{{ reply.short }}.</strong> {{ reply.text }} Your selected components are included.</span>
        </div>
        <div class="quote-fields">
          <v-text-field v-model="data.name" name="name" autocomplete="name"
            label="Full name *" :rules="rules.name" outlined dense color="bb-primary" :disabled="loading"/>
          <v-text-field v-model="data.email" type="email" name="email" autocomplete="email"
            label="Email address *" :rules="rules.email" outlined dense color="bb-primary" :disabled="loading"/>
          <v-text-field v-model="data.phone" type="tel" name="tel" autocomplete="tel"
            label="Phone number *" :rules="rules.phone" outlined dense color="bb-primary" :disabled="loading"/>
          <v-select v-model="data.location" :items="locations" label="Preferred location *"
            :rules="rules.location" outlined dense color="bb-primary" :disabled="loading"/>
          <v-textarea v-model="data.message" class="quote-message" label="Tell us about your ride *"
            placeholder="Your riding style, sizing questions or anything you’d like us to know…"
            :rules="rules.message" rows="3" outlined no-resize color="bb-primary" :disabled="loading"/>
        </div>
        <v-alert v-if="error" type="error" outlined dense role="alert">
          We couldn’t send your enquiry. Your details are still here — please try again.
        </v-alert>
        <footer class="quote-footer">
          <p>All fields are required.<br>Your build, with expert advice.</p>
          <Btn color="bb-primary" type="submit" :loading="loading" :disabled="loading" dark>
            {{ reply.today ? 'Get my answer today' : 'Request my quote' }} <span aria-hidden="true" class="ml-3">→</span>
          </Btn>
        </footer>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>

  import { Btn } from '@/components';
  import { appBase } from '@/utils/basePath';
  import { capitalize } from '@/utils';
  import { replyPromise } from '@/utils/replyPromise';
  import { mapState } from 'vuex';

  const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  // RULES

  const required = v => !!(v||'').trim() || 'The field is required.';
  const email = v => EMAIL_REGEX.test(v) || 'The email is not valid.';
  const shortest = n => v => (v||'').trim().length > n || 'The field is too short.';

  export default {
    components: { Btn },
    props: {
      value: null,
      tyreNotice: String,
      items: Array
    },
    data() {
      return {
        show: !!this.value,
        valid: false,
        loading: false,
        sended: false,
        reply: replyPromise(),
        error: false,
        data: {
          name: '',
          email: '',
          phone: '',
          location: '',
          message: ''
        },
        locations: ['Barcelona', 'Girona', 'Ship to my home'],
        rules: {
          name: [ required, shortest(2) ],
          email: [ required, email ],
          phone: [ required ],
          location: [ required ],
          message: [ required ]
        }
      }
    },
    watch: {
      value( value ) {
        this.show = !!value;
      },
      show( value ) {
        value && this.reset();
        this.$emit( 'input', this.show );
      }
    },
    computed: {
      ...mapState([ 'company' ]),
      logoUrl() { return appBase + '/velodrom-logo.png'; },
      isMobile() {
        return this.$vuetify.breakpoint.width <= 480;
      }
    },
    methods: {
      reset() {
        this.reply = replyPromise();
        this.sended = this.error = false;
        this.data = { name: '', email: '', phone: '', location: '', message: '' };
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.resetValidation();
        });
      },
      send() {
        if ( this.loading || !this.$refs.form.validate() ) return;
        if ( this.valid ) {

          const params = {
            ...this.data,
            idCompany: this.company.id,
            url: window.location.href,
            idFramesetColor: -1,
            idSeatpostColor: -1,
            idBarColor: -1,
            idSaddleColor: -1,
            idGroupsetColor: -1,
            idWheelColor: -1,
            idTyreColor: -1
          };

          // Add composition
          this.items.forEach(( a, key ) => {
            key = 'id' + capitalize( a.item.step.id ).replace(/s$/,'') + 'Color';
            params[key] = a.item.colors[ a.color ].color.id;
            if (a.item.type === 'tyres') params.tyreWidthMm = a.tyreWidth || 'Not selected';
          });
          if (this.tyreNotice) params.tyreClearance = this.tyreNotice;

          // Send request
          this.error = false;
          this.loading = true;
          this.$store
            .dispatch( 'buyBike', params )
            .then( res => {
              if ( res.data.error ) {
                console.error( res.data.message );
                this.error = true;
              } else {
                this.sended = true;
              }
            })
            .catch( err => {
              console.error( err );
              this.error = true;
            })
            .finally(() => this.loading = false );
        }
      }
    }
  }
</script>

<style>
.quote-dialog { border-radius: 20px !important; overflow: hidden !important; }
.contact-form { display: flex; flex-direction: column; max-height: 90vh; overflow-y: auto; color: #263027; }
.quote-header { position: relative; padding: 24px 36px; background: linear-gradient(120deg, #304635, #52634b); color: #fff; }
.quote-brand { display: flex; align-items: center; gap: 22px; padding-right: 32px; margin-bottom: 20px; }
.quote-brand img { width: 142px; height: auto; filter: brightness(0) invert(1); }
.quote-brand span { font-size: 10px; letter-spacing: 2px; opacity: .8; }
.quote-close { position: absolute; top: 20px; right: 20px; width: 36px; height: 36px; border-radius: 50%; background: rgba(255,255,255,.12); }
.quote-close:focus-visible { outline: 2px solid white; outline-offset: 3px; }
.quote-eyebrow { font-size: 10px; letter-spacing: 2px; margin-bottom: 10px !important; color: #d9e2d0; }
.quote-header h2 { text-transform: none; font-size: 29px; line-height: 1.2; font-weight: 600; letter-spacing: -.7px; margin-bottom: 12px; }
.quote-header > p:last-child { font-size: 14px; line-height: 1.65; max-width: 560px; opacity: .85; margin: 0; }
.quote-form { padding: 24px 36px 28px; }
.quote-context { display: flex; gap: 10px; align-items: center; padding: 12px 14px; background: #f1f3ed; border-radius: 9px; color: #52634b; font-size: 12px; margin-bottom: 24px; }
.quote-promise { background: #e4ebdf; color: #304635; font-size: 13px; line-height: 1.5; }
.quote-promise strong { font-weight: 600; }
.quote-fields { display: grid; grid-template-columns: 1fr 1fr; column-gap: 20px; }
.quote-message { grid-column: 1 / -1; }
.contact-form .v-input__slot { border-radius: 9px; background: #fcfcfa !important; }
.contact-form .v-text-field--outlined fieldset { border-color: #d6dbd1; }
.contact-form .v-label { font-size: 14px; }
.contact-form .v-text-field input, .contact-form textarea { font-size: 16px; }
.quote-footer { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding-top: 8px; }
.quote-footer p { font-size: 11px; line-height: 1.65; color: #717a70; margin: 0; }
.quote-footer .v-btn { background-color: #304635 !important; color: white !important; border-radius: 9px; min-height: 48px; letter-spacing: .5px; }
.quote-success { padding: 40px 36px; text-align: center; }
.quote-success-icon { width: 52px; height: 52px; line-height: 52px; border-radius: 50%; background: #e9eee2; color: #304635; font-size: 25px; margin: 0 auto 20px; }
.quote-success h3 { font-size: 23px; margin-bottom: 12px; }
.quote-success p { color: #717a70; line-height: 1.7; margin-bottom: 24px; }
@media (max-width: 480px) {
  .quote-dialog { border-radius: 0 !important; }
  .contact-form { max-height: 100%; min-height: 100%; }
  .quote-header { padding: 26px 22px; }
  .quote-header h2 { font-size: 26px; }
  .quote-brand { gap: 14px; margin-bottom: 24px; }
  .quote-brand img { width: 120px; }
  .quote-brand span { font-size: 8px; letter-spacing: 1px; }
  .quote-form { padding: 22px; }
  .quote-fields { grid-template-columns: 1fr; }
  .quote-footer { flex-direction: column; align-items: stretch; }
  .quote-footer p { text-align: center; }
}
</style>
